import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Package, 
  MapPin, 
  CreditCard,
  Eye,
  RefreshCw,
  Filter,
  Search,
  ChevronDown,
  Truck
} from 'lucide-react';
import api, { getImageUrl } from '../utils/api';

interface Order {
  _id: string;
  orderNumber: string;
  totalAmount: number;
  paymentMethod: string;
  paymentStatus: string;
  orderStatus: string;
  createdAt: string;
  shippingAddress: {
    name: string;
    city: string;
    state: string;
  };
  items: Array<{
    product: {
      _id: string;
      name: string;
      images: string[];
      price: number;
    };
    quantity: number;
    price: number;
  }>;
}

const Orders: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [isAuthenticated, navigate]);

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/orders/my-orders');
      setOrders(response.data.orders || []);
    } catch (error: any) {
      console.error('Error fetching orders:', error);
      setError(error.response?.data?.message || 'Failed to fetch orders');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      case 'processing':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400';
      case 'shipped':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400';
      case 'delivered':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const filteredAndSortedOrders = orders
    .filter(order => {
      const matchesSearch = order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           order.items.some(item => item.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = statusFilter === 'all' || order.orderStatus.toLowerCase() === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'oldest':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'amount-high':
          return b.totalAmount - a.totalAmount;
        case 'amount-low':
          return a.totalAmount - b.totalAmount;
        default:
          return 0;
      }
    });

  if (!isAuthenticated) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading your orders...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-secondary-900 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1 sm:mb-2">
              My Orders
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              Track and manage your order history
            </p>
          </div>
          <button
            onClick={fetchOrders}
            className="mt-3 sm:mt-0 flex items-center justify-center px-4 py-2 bg-primary-600 hover:bg-primary-700 
                     dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-lg transition-colors w-full sm:w-auto"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>

        {/* Mobile-First Filters */}
        <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-gray-200 dark:border-secondary-700 p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="space-y-4">
            {/* Search - Full width on mobile */}
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by order ID or product..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-3 py-2.5 border border-gray-300 dark:border-secondary-600 
                         bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 text-sm sm:text-base
                         rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Filter Row - Side by side on mobile */}
            <div className="grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-3">
              {/* Status Filter */}
              <div className="relative">
                <Filter className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 z-10" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="pl-10 w-full px-3 py-2.5 border border-gray-300 dark:border-secondary-600 
                           bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 text-sm sm:text-base
                           rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none pr-8"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Sort */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-secondary-600 
                           bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 text-sm sm:text-base
                           rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 appearance-none pr-8"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="amount-high">High to Low</option>
                  <option value="amount-low">Low to High</option>
                </select>
                <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Orders List */}
        {filteredAndSortedOrders.length === 0 ? (
          <div className="bg-white dark:bg-secondary-800 rounded-lg shadow-md p-12 text-center">
            <Package className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {orders.length === 0 ? 'No orders yet' : 'No orders match your filters'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {orders.length === 0 
                ? 'Start shopping to see your orders here!' 
                : 'Try adjusting your search or filter criteria.'
              }
            </p>
            <Link
              to="/products"
              className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 
                       dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-6">
            {filteredAndSortedOrders.map((order) => (
              <div key={order._id} className="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-gray-200 dark:border-secondary-700 overflow-hidden">
                {/* Mobile-Optimized Order Header */}
                <div className="bg-gray-50 dark:bg-secondary-700/50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-secondary-600">
                  <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
                    {/* Order Info - Stacked on mobile */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-6">
                      <div>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Order ID</p>
                        <p className="font-semibold text-sm sm:text-base text-gray-900 dark:text-gray-100">
                          #{order._id.slice(-8).toUpperCase()}
                        </p>
                      </div>
                      <div className="mt-1 sm:mt-0">
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Date</p>
                        <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-gray-100">
                          {formatDate(order.createdAt)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Status Badges - Stacked on mobile */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.orderStatus)}`}>
                        {order.orderStatus.charAt(0).toUpperCase() + order.orderStatus.slice(1)}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(order.paymentStatus)}`}>
                        {order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Mobile-Optimized Order Body */}
                <div className="p-4 sm:p-6">
                  <div className="space-y-4 sm:space-y-6">
                    {/* Items Section - Improved Mobile Layout */}
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 text-sm sm:text-base">Ordered Items</h4>
                      <div className="space-y-3">
                        {order.items.slice(0, 2).map((item, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <img
                              src={getImageUrl(item.product?.images?.[0] || '')}
                              alt={item.product?.name || 'Product Unavailable'}
                              className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-lg flex-shrink-0"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '/placeholder-image.png';
                              }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm sm:text-base">
                                {item.product?.name || 'Product Unavailable'}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                Qty: {item.quantity} × ₹{item.price.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                        {order.items.length > 2 && (
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 pl-13">
                            +{order.items.length - 2} more items
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Order Summary - Mobile Card Layout */}
                    <div className="bg-gray-50 dark:bg-secondary-700/30 rounded-lg p-3 sm:p-4 space-y-3">
                      {/* Total Amount - Prominent */}
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 dark:text-gray-100 text-sm sm:text-base">Total Amount</span>
                        <span className="font-bold text-lg sm:text-xl text-primary-600 dark:text-primary-400">
                          ₹{order.totalAmount.toLocaleString()}
                        </span>
                      </div>
                      
                      {/* Payment & Delivery Info */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                        <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{order.paymentMethod}</span>
                        </div>
                        
                        <div className="flex items-start text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          <MapPin className="h-3 w-3 sm:h-4 sm:w-4 mr-2 mt-0.5 flex-shrink-0" />
                          <div className="truncate">
                            <p className="truncate">{order.shippingAddress.name}</p>
                            <p className="truncate">{order.shippingAddress.city}, {order.shippingAddress.state}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons - Mobile Optimized */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <Link
                        to={`/orders/${order._id}`}
                        className="flex items-center justify-center px-4 py-2.5 border border-gray-300 dark:border-secondary-600 
                                 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-secondary-700 
                                 transition-colors text-sm font-medium"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </Link>
                      {(order.orderStatus === 'shipped' || order.orderStatus === 'processing') && (
                        <button className="flex items-center justify-center px-4 py-2.5 bg-primary-600 hover:bg-primary-700 
                                         dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-lg transition-colors text-sm font-medium">
                          <Truck className="h-4 w-4 mr-2" />
                          Track Order
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Orders;